import {
  add,
  addDays,
  addMinutes,
  differenceInMinutes,
  type Duration,
  isBefore,
  parse,
  parseISO,
  sub,
  subMinutes,
} from 'date-fns'
import { List, Map } from 'immutable'

import { EventExtendedProps } from '../../../models/calendar/CalendarV2'
import { convertProviderTreatmentModalityToTreatmentOption } from '../../common/constants/providerTreatmentModalityConstants'
import { getTimezoneOffset, toLocalDate, toLocalISOString, toUTCISOString } from '../../common/utils/timeZoneUtils'

// Re-export timezone utilities from their canonical location for backwards compatibility
export { getTimezoneOffset, toLocalDate, toLocalISOString, toUTCISOString }
export enum OffsetDirection {
  BEFORE_AND_AFTER = 'BEFORE_AND_AFTER',
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  NONE = 'NONE',
}

export const getAdjustedEvent: (
  event: { startStr: string; endStr: string },
  startingRange: { startStr: string; endStr: string } | null,
) => {
  start: string
  end: string
  offset: OffsetDirection
} = (event, startingRange) => {
  //ISO dates from this library do not include tz info so considering as UTC while processing
  const startTime = parseISO(event.startStr + 'Z')
  const endTime = parseISO(event.endStr + 'Z')
  const differenceHours = differenceInMinutes(endTime, startTime) / 60

  if (!startingRange || startingRange.startStr === event.startStr) {
    return {
      start: startTime.toISOString(),
      end: differenceHours % 1 !== 0 ? addMinutes(endTime, 30).toISOString() : endTime.toISOString(),
      offset: differenceHours % 1 !== 0 ? OffsetDirection.AFTER : OffsetDirection.NONE,
    }
  }
  return {
    start: differenceHours % 1 !== 0 ? startTime.toISOString() : subMinutes(startTime, 30).toISOString(),
    end: addMinutes(endTime, 30).toISOString(),
    offset: differenceHours % 1 !== 0 ? OffsetDirection.BEFORE : OffsetDirection.BEFORE_AND_AFTER,
  }
}

export const eventsOverlap = (a: { start: Date; end: Date }, b: { start: Date; end: Date }): boolean => {
  if (a.start < b.start && b.start < a.end) return true
  if (a.start < b.end && b.end < a.end) return true // b ends in a
  if (b.start <= a.start && a.end <= b.end) return true // a in b
  return false
}

export const isWithinWorkingHours = ({
  event,
  workingHours,
  timeZone,
}: {
  event: { start: string; end: string }
  workingHours: { start: string; end: string }
  timeZone: string
}): boolean => {
  const eventStartTime = toLocalDate(event.start, timeZone)
  const eventEndTime = toLocalDate(event.end, timeZone)
  const workingHoursStart = parse(workingHours.start ?? '07:00:00', 'HH:mm:ss', eventStartTime)
  let workingHoursEnd = parse(workingHours.end ?? '22:00:00', 'HH:mm:ss', eventEndTime)
  if (isBefore(workingHoursEnd, workingHoursStart)) {
    workingHoursEnd = addDays(workingHoursEnd, 1)
  }
  return eventStartTime >= workingHoursStart && eventEndTime <= workingHoursEnd
}

export const isBookableEvent = (extendedProps: EventExtendedProps) =>
  ['bookable', 'bookable_recurring'].includes(extendedProps.lyraEventType)

export const isSessionOrSlot = (extendedProps: EventExtendedProps) => {
  return isBookableEvent(extendedProps) || extendedProps.lyraEventType === 'session'
}

export const isOOOEvent = (title: string | undefined) =>
  title !== undefined && /^\s*(OOO|PTO|Vacation|Sick|UglyPopcorn)([^a-z]|$)/i.test(title)

export type DurationRange = {
  before?: Duration
  after?: Duration
}

export const makeDurationRange = (range: DurationRange): [string, string] => {
  let startDate = new Date()
  let endDate = new Date()

  if (range.before) {
    startDate = sub(startDate, range.before)
  }

  if (range.after) {
    endDate = add(endDate, range.after)
  }

  return [startDate.toISOString(), endDate.toISOString()]
}

export interface CreateRescheduleAppointmentPayloadParams {
  providerID?: string | null
  providerModalities?: List<string> | null
  userID?: string
  appointment: Map<string, unknown>
  referenceAppointment: Map<string, unknown>
  defaultTitle?: string
}

/**
 * Creates a payload for rescheduling an appointment.
 * This is a crossplatform version that includes guardianLyraId support.
 */
export const createRescheduleAppointmentPayload = ({
  providerID = null,
  providerModalities = null,
  userID,
  appointment,
  referenceAppointment,
  defaultTitle,
}: CreateRescheduleAppointmentPayloadParams) => {
  const treatmentType = appointment.get('treatmentType', referenceAppointment.get('treatmentType')) as string
  const modalities = providerModalities ?? (appointment.getIn(['provider', 'modalities'], List()) as List<string>)
  const selectedProviderModality = modalities.find(
    (modality) => modality != null && convertProviderTreatmentModalityToTreatmentOption(modality) === treatmentType,
  )

  const guardianLyraId = appointment.get('guardianLyraId', referenceAppointment.get('guardianLyraId')) as
    | string
    | undefined
  const programId = appointment.get('programId', referenceAppointment.get('programId')) as string | undefined

  const payload = {
    address: appointment.get('address', referenceAppointment.get('address', '')),
    appointmentDuration: appointment.get('appointmentDuration', 50),
    appointmentId: appointment.get('appointmentId'),
    appointmentStatus: appointment.get('appointmentStatus', referenceAppointment.get('appointmentStatus')),
    appointmentType: appointment.get('appointmentType', referenceAppointment.get('appointmentType', 'googleCalendar')),
    details: appointment.get('details', referenceAppointment.get('details') || ''),
    eventId: appointment.get('eventId'),
    ...(guardianLyraId && { guardianLyraId }),
    ...(programId && { programId }),
    lyraProviderId: providerID || appointment.get('lyraProviderId', referenceAppointment.get('lyraProviderId')),
    meetingFormat: appointment.get('meetingFormat', 'video'),
    phone: appointment.get('phone', referenceAppointment.get('phone')),
    phoneType: appointment.get('phoneType', referenceAppointment.get('phoneType')),
    selectedProviderModality,
    startDate: appointment.get('startDate'),
    startTime: appointment.get('startTime'),
    supportedVisitTypes: appointment.get('supportedVisitTypes', referenceAppointment.get('supportedVisitTypes') || 1),
    textAppDownloadLink: false,
    title: appointment.get('title', defaultTitle ?? 'New Appointment'),
    timeZone: appointment.get('timeZone'),
    treatmentType: appointment.get('treatmentType', referenceAppointment.get('treatmentType')),
    userId: userID || appointment.get('userId', referenceAppointment.get('userId')),
  }
  return payload
}
